// src/components/DashboardQuick.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Paper, Typography, Skeleton, Box, Stack, Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AreaChart, Area, Line, CartesianGrid, XAxis, YAxis, Tooltip, BarChart, Bar } from 'recharts';
import axiosInstance from '../api/axiosInstance';

const fmtNum  = (n, d = 0) => (Number.isFinite(n) ? new Intl.NumberFormat('es-PE',{maximumFractionDigits:d}).format(n) : '—');
const fmtDate = (s) => { if(!s) return ''; const [y,m,d] = String(s).split('-'); return `${d}/${m}`; };

function StatCard({ title, value, color }) {
  return (
    <Paper sx={{
      p:2, height:'100%', width: 1, borderRadius:2,
      boxShadow:'0 4px 18px rgba(0,0,0,0.06)',
      borderLeft:(t)=>`6px solid ${color || t.palette.primary.main}`
    }}>
      <Stack spacing={0.5}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
      </Stack>
    </Paper>
  );
}

/** AutoSizer: mide ancho real y lo pasa al chart */
function AutoSizer({ height, children }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width || el.clientWidth || 0;
      setW(width);
    });
    ro.observe(el);
    setW(el.clientWidth || 0);
    return () => ro.disconnect();
  }, []);
  return (
    <Box ref={ref} sx={{ width: '100%', height }}>
      {w > 0 ? children(w) : <Skeleton variant="rectangular" height={height} />}
    </Box>
  );
}

export default function DashboardQuick({ desde, hasta, renaes, idUps }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const chartH = isMdUp ? 420 : 360;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const qs = useMemo(() => {
    const q = new URLSearchParams();
    if (desde)  q.set('desde',  desde);
    if (hasta)  q.set('hasta',  hasta);
    if (renaes) q.set('renaes', renaes);
    if (idUps)  q.set('id_ups', idUps);
    return q.toString();
  }, [desde, hasta, renaes, idUps]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axiosInstance.get(`/dash/summary${qs ? `?${qs}` : ''}`)
      .then(res => { if (mounted) setData(res.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [qs]);

  const k     = data?.kpis || {};
  const serie = data?.serie || [];
  const top   = (data?.top_codigos || [])
                  .slice()
                  .sort((a,b)=> (b?.conteo||0) - (a?.conteo||0))
                  .slice(0,5);
  const top1  = top?.[0]?.Codigo_Item || '—';

  const maxSerie = useMemo(() => serie.reduce((m, d) => Math.max(m, Number(d?.citas)||0), 0), [serie]);
  const yMax = Math.ceil(maxSerie * 1.1) || 'auto';

  const primary = theme.palette.primary.main;
  const success = theme.palette.success.main;
  const info    = theme.palette.info.main;
  const warn    = theme.palette.warning.main;
  const textM   = theme.palette.text.secondary;

  const StatValue = ({ v, d=0 }) => (loading ? <Skeleton width={100}/> : fmtNum(v, d));

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {/* KPIs */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><StatCard title="CITAS (RANGO)"          value={<StatValue v={k.citas}/>}                 color={primary}/></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="PACIENTES ÚNICOS (DÍA)" value={<StatValue v={k.pacientes_unicos_sum}/>} color={success}/></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="HEMOGLOBINA PROM."      value={<StatValue v={k.hemoglobina_prom} d={2}/>} color={info}/></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard title="TOP CÓDIGO (RANGO)"     value={loading ? <Skeleton width={120}/> : top1} color={warn}/></Grid>
      </Grid>

      {/* GRÁFICOS: dos columnas desde SM; en XS se apilan */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, // responsive
          gap: 2,
          width: '100%',
          alignItems: 'stretch',
        }}
      >
        {/* Citas por día */}
        <Paper sx={{ p:2, borderRadius:2, boxShadow:'0 4px 18px rgba(0,0,0,0.06)', width:'100%', height: chartH, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb:1, fontWeight:700 }}>Citas por día</Typography>
          <Divider sx={{ mb:1 }} />
          {loading ? (
            <Skeleton variant="rectangular" height={chartH - 60} />
          ) : serie.length === 0 ? (
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:chartH - 60, color: textM }}>
              <Typography variant="body2">Sin datos para el rango seleccionado</Typography>
            </Box>
          ) : (
            <AutoSizer height={chartH - 60}>
              {(w) => (
                <AreaChart width={w} height={chartH - 60} data={serie} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <defs>
                    <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={primary} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Fecha" tickFormatter={fmtDate} tickMargin={8} />
                  <YAxis domain={[0, yMax]} tickFormatter={(v)=>fmtNum(v)} />
                  <Tooltip
                    formatter={(val, name) => [fmtNum(val), name]}
                    labelFormatter={(l)=>`Fecha: ${l}`}
                    contentStyle={{ background: theme.palette.background.paper, borderRadius: 8, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Area type="monotone" dataKey="citas" stroke={primary} fill="url(#gradArea)" strokeWidth={2} />
                  <Line type="monotone" dataKey="citas" stroke={primary} strokeWidth={2} dot={false} />
                </AreaChart>
              )}
            </AutoSizer>
          )}
        </Paper>

        {/* Top 5 códigos */}
        <Paper sx={{ p:2, borderRadius:2, boxShadow:'0 4px 18px rgba(0,0,0,0.06)', width:'100%', height: chartH, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb:1, fontWeight:700 }}>Top 5 códigos</Typography>
          <Divider sx={{ mb:1 }} />
          {loading ? (
            <Skeleton variant="rectangular" height={chartH - 60} />
          ) : top.length === 0 ? (
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:chartH - 60, color: textM }}>
              <Typography variant="body2">Sin datos para el rango seleccionado</Typography>
            </Box>
          ) : (
            <AutoSizer height={chartH - 60}>
              {(w) => (
                <BarChart
                  width={w} height={chartH - 60}
                  data={[...top].reverse()}
                  layout="vertical"
                  margin={{ top: 10, right: 16, left: 28, bottom: 8 }}
                  barSize={22} barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v)=>fmtNum(v)} />
                  <YAxis type="category" dataKey="Codigo_Item" width={80} />
                  <Tooltip
                    formatter={(val, name) => [fmtNum(val), name]}
                    contentStyle={{ background: theme.palette.background.paper, borderRadius: 8, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Bar dataKey="conteo" fill={success} />
                </BarChart>
              )}
            </AutoSizer>
          )}
        </Paper>
      </Box>
    </Stack>
  );
}
